import React from 'react';
import { Box, ListItem, Stack, Text, UnorderedList } from '@chakra-ui/react';
import { PropertyRoute } from '@common/entities';

interface PropertyConflictWarningProps {
  propertyRoutes: PropertyRoute[];
  action: 'published' | 'saved';
}

/**
 * Generates a warning message for property route conflicts
 * @param propertyRoutes - Array of property routes that will be affected
 * @param action - The action being performed ('published' or 'saved')
 * @returns React element with formatted warning message
 */
export function PropertyConflictWarning({
  propertyRoutes,
  action,
}: PropertyConflictWarningProps): React.ReactElement {
  const actionText = action === 'published' ? 'Publishing' : 'Saving';

  return (
    <Stack spacing={4} align="stretch">
      {/* Header - Centered */}
      <Box textAlign="center">
        <Text fontWeight="600" fontSize="md" color="orange.darkest" mb={2}>
          Property Reassignment Notice
        </Text>
        <Text fontSize="sm" color="gray.dark">
          {actionText} this route will reassign the following properties from their current routes
          to this route.
        </Text>
      </Box>

      {/* Properties List */}
      <Box>
        <Text fontWeight="600" fontSize="sm" mb={2} color="black.base">
          The following properties will be moved from their current routes:
        </Text>
        <Stack spacing={2} maxH="180px" overflowY="auto">
          {propertyRoutes.map((route, index) => (
            <Box
              key={index}
              bg="gray.highlighthalf"
              p={3}
              borderRadius="md"
              border="1px solid"
              borderColor="gray.light"
            >
              <Text fontSize="sm" fontWeight="600" color="black.base">
                {route.propertyName} → {route.routeName}
              </Text>
            </Box>
          ))}
        </Stack>
      </Box>

      {/* Impact Message */}
      <Box
        bg="gray.highlighthalf"
        p={3}
        borderRadius="md"
        border="1px solid"
        borderColor="gray.light"
      >
        <Text fontWeight="600" mb={2}>
          How these routes will be affected:
        </Text>
        <UnorderedList textAlign="left" spacing={3} pl={6}>
          <ListItem>The listed properties will be removed from their current routes</ListItem>
          <ListItem>The properties will be assigned to this route</ListItem>
          <ListItem>
            Any routes with no remaining properties will be marked as{' '}
            <Text as="span" fontWeight="700" color="orange.dark">
              Inactive
            </Text>
          </ListItem>
        </UnorderedList>
      </Box>

      {/* Confirmation Question */}
      <Text color="gray.dark" textAlign="center" mt={4}>
        Do you want to proceed?
      </Text>
    </Stack>
  );
}

/**
 * Generates a success message for routes with no conflicts
 * @param action - The action being performed ('publish' or 'save')
 * @returns React element with confirmation message
 */
export function NoConflictMessage(action: 'publish' | 'save'): React.ReactElement {
  const actionText = action === 'publish' ? 'published' : 'saved';
  const actionResult =
    action === 'publish'
      ? 'make this route active for all properties associated with it'
      : 'update this route with the new settings';

  return (
    <Stack spacing={3} align="stretch">
      <Text fontSize="sm" color="black.base">
        This route will be {actionText}. This will {actionResult}.
      </Text>
      <Text fontSize="sm" color="gray.dark" textAlign="center">
        Do you want to continue?
      </Text>
    </Stack>
  );
}
